/* Shell widgets: credit meter on real billing data, notification inbox, approvals badge, the global agent
   kill switch, the AI disclosure link, the install button and reactive nav badges. Every count and balance
   comes from the API; without it a widget hides (never sample data), and each works without the feature it
   borrows from. */
import { useMemo, useState, useSyncExternalStore, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, BellOff, CheckCheck, Download, Info, OctagonX, Settings } from 'lucide-react';
import { Button, CreditMeter, Dialog, Menu, useToast } from '../ui';
import { OptionalSlot, optionalComponent } from '../ui/optional';
import { api, ApiError } from '../lib/api';
import { API } from '../lib/endpoints';
import type { FeatureDescriptor } from './feature';
import { useInstallPrompt } from './pwa';
import { useSession } from './session';

/* ---------- credits (BILL-02) ---------- */
export interface CreditView { used: number; allowance: number; resetsAt?: string; addon?: number; byok: boolean; source: 'billing' }

interface SummaryLite {
  mode?: { current?: string };
  balance?: { available?: number; trial?: number; bonus?: number; purchased?: number; allowance?: { limit: number; used: number } | null };
  allowance?: { limit: number; used: number; resets?: { monthly?: string | null } } | null;
}

const shortDate = (iso?: string | null) => {
  if (!iso) return undefined;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? undefined : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/** Maps GET /api/v1/billing/summary onto the meter: allowance used/limit, purchased + bonus as add-on, BYOK. */
export function creditsFromSummary(s: SummaryLite): CreditView {
  const byok = s.mode?.current === 'byok';
  const al = s.allowance ?? s.balance?.allowance ?? null;
  const extra = (s.balance?.purchased ?? 0) + (s.balance?.bonus ?? 0);
  if (al) return { used: al.used, allowance: al.limit, resetsAt: shortDate(s.allowance?.resets?.monthly), addon: extra || undefined, byok, source: 'billing' };
  // Pay as you go and the trial have no allowance: the meter shows what is left to spend.
  const available = Math.max(0, s.balance?.available ?? extra + (s.balance?.trial ?? 0));
  return { used: 0, allowance: available, byok, source: 'billing' };
}

/** The meter reads GET /billing/summary only; while it loads, fails or is not set up, there is no meter (never a stand-in balance). */
export function useCredits(): CreditView | null {
  const session = useSession();
  const live = session.status === 'authenticated';
  const q = useQuery({
    queryKey: ['billing', 'summary', 'meter'],
    queryFn: async () => (await api.get<{ summary: SummaryLite }>(API.billingSummary)).summary,
    enabled: live,
    staleTime: 30_000,
    refetchInterval: live ? 60_000 : false,
    retry: false,
  });
  return useMemo(() => (live && q.data ? creditsFromSummary(q.data) : null), [live, q.data]);
}

export function CreditMeterSlot({ compact }: { compact?: boolean }) {
  const credits = useCredits();
  const navigate = useNavigate();
  if (!credits) return null;
  return (
    <CreditMeter used={credits.used} allowance={credits.allowance} resetLabel={credits.resetsAt} addon={credits.addon} byok={credits.byok} compact={compact} onClick={() => navigate('/billing')} />
  );
}

/* ---------- notifications ---------- */
export interface NotificationItem { id: string; type: string; title: string; body?: string | null; link?: string | null; readAt?: string | null; createdAt: string }

const ago = (iso: string) => {
  const m = Math.round((Date.now() - Date.parse(iso)) / 60_000);
  if (!Number.isFinite(m)) return '';
  if (m < 1) return 'now';
  if (m < 60) return `${m} min`;
  if (m < 48 * 60) return `${Math.round(m / 60)} h`;
  return `${Math.round(m / 1440)} d`;
};

export function useNotifications(refetchMs = 60_000) {
  const session = useSession();
  const qc = useQueryClient();
  const live = session.status === 'authenticated';
  const q = useQuery({
    queryKey: ['notifications', 'inbox'],
    queryFn: () => api.get<{ notifications: NotificationItem[]; unread: number }>(API.notifications, { query: { limit: 20 } }),
    enabled: live,
    refetchInterval: live ? refetchMs : false,
    staleTime: 15_000,
    retry: false,
  });
  const markRead = useMutation({
    mutationFn: (id: string) => api.post(API.notificationRead(id)),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['notifications', 'inbox'] }),
  });
  const markAll = useMutation({
    mutationFn: () => api.post(API.notificationsReadAll),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['notifications', 'inbox'] }),
  });
  return {
    items: q.data?.notifications ?? [], unread: q.data?.unread ?? 0, available: !!q.data,
    read: (id: string) => markRead.mutate(id),
    readAll: () => markAll.mutate(),
  };
}

export function NotificationsButton() {
  const n = useNotifications();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  if (!n.available) return null;
  const list = (
    <ul className="ntf-list" aria-label="Notifications">
      {!n.items.length && <li className="small muted ntf-empty"><BellOff size={16} aria-hidden="true" /> Nothing new.</li>}
      {n.items.map((x) => (
        <li key={x.id}>
          <button
            type="button"
            className="ntf-item"
            data-unread={!x.readAt || undefined}
            onClick={() => { if (!x.readAt) n.read(x.id); setOpen(false); if (x.link) navigate(x.link); }}
          >
            <span className="ntf-dot" aria-hidden="true" />
            <span className="ntf-text"><b>{x.title}</b>{x.body && <span className="small muted">{x.body}</span>}</span>
            <span className="small muted ntf-when">{ago(x.createdAt)}{!x.readAt && <span className="sr-only"> (unread)</span>}</span>
          </button>
        </li>
      ))}
    </ul>
  );
  return (
    <Menu
      label="Notifications"
      align="end"
      asSheet="auto"
      open={open}
      onOpenChange={setOpen}
      trigger={(p) => (
        <button type="button" className="icon-btn tb-badged" {...p} ref={p.ref} aria-label={`Notifications${n.unread ? `: ${n.unread} unread` : ''}`} title="Notifications" data-cap="PLAT-06">
          <Bell size={18} aria-hidden="true" />
          {n.unread > 0 && <span className="badge tb-count" aria-hidden="true">{n.unread > 99 ? '99+' : n.unread}</span>}
        </button>
      )}
      groups={[
        { id: 'inbox', label: n.unread ? `Notifications (${n.unread} new)` : 'Notifications', wide: true, content: list },
        {
          id: 'manage', label: 'Manage', items: [
            { id: 'all', label: 'Mark all as read', icon: CheckCheck, disabled: !n.unread, onSelect: n.readAll },
            { id: 'settings', label: 'Notification settings', icon: Settings, onSelect: () => navigate('/settings/personal#notifications') },
          ],
        },
      ]}
    />
  );
}

/* ---------- approvals badge (AGENT-12) ---------- */
type ApprovalInboxProps = { variant: 'badge' | 'list'; onOpenTask?: (taskId: string) => void; refetchMs?: number };
const ApprovalInbox = optionalComponent<ApprovalInboxProps>(import.meta.glob('../features/agents/ApprovalInbox.tsx'), 'ApprovalInbox');

/** Pending approvals across every task; polls while mounted and keeps the agents nav badge current. */
export function ApprovalsBadge() {
  const navigate = useNavigate();
  if (!ApprovalInbox) return null;
  return (
    <span className="tb-approvals">
      <OptionalSlot C={ApprovalInbox} props={{ variant: 'badge', refetchMs: 15_000, onOpenTask: (id) => navigate(`/agents?task=${encodeURIComponent(id)}`) }} />
    </span>
  );
}

/* ---------- global kill switch (AGENT-11, research rule 30: always reachable) ---------- */
export function useKillSwitch() {
  const toast = useToast();
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => api.post<{ stopped: number }>(API.agentKill),
    onSuccess: (r) => {
      const n = r?.stopped ?? 0;
      toast.show(n ? `Stopped ${n} agent task${n === 1 ? '' : 's'}. Pending approvals were cancelled.` : 'No agents were running.');
      void qc.invalidateQueries({ queryKey: ['agents'] });
    },
    onError: (e) => toast.show(e instanceof ApiError && e.isNetwork ? 'Could not reach the service to stop agents. Try again.' : `Could not stop agents: ${(e as Error).message}`, { tone: 'danger' }),
  });
  return m;
}

export function KillSwitchDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const kill = useKillSwitch();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Stop all agents?"
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="danger" disabled={kill.isPending} onClick={() => kill.mutate(undefined, { onSettled: onClose })}>Stop all agents</Button></>}
    >
      <p data-cap="AGENT-11">Every running agent task stops at once and its pending approvals are cancelled. Finished work and files are kept. Nothing restarts on its own.</p>
    </Dialog>
  );
}

export function KillSwitchButton({ onOpen }: { onOpen: () => void }) {
  return (
    <button type="button" className="icon-btn tb-kill" onClick={onOpen} aria-label="Stop all agents (kill switch)" title="Stop all agents" data-cap="AGENT-11">
      <OctagonX size={18} aria-hidden="true" />
    </button>
  );
}

/* ---------- AI disclosure (SAFE-01, rule 37) ---------- */
export function AiDisclosureLink({ children }: { children?: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="link ai-disc" onClick={() => setOpen(true)} data-cap="SAFE-01">
        <Info size={12} aria-hidden="true" /> {children ?? 'AI-generated content'}
      </button>
      <Dialog open={open} onClose={() => setOpen(false)} title="About AI-generated content" footer={<Button variant="primary" onClick={() => setOpen(false)}>Got it</Button>}>
        <div className="stack small">
          <p>Replies, documents, images, audio and video made by Crosspoint AI + are generated by AI models. They can be wrong, so check anything that matters, especially health, legal and money questions.</p>
          <p>Everything generated carries an <span className="ai-pill">AI</span> label. Images, audio and video also carry Content Credentials (the <b>CR</b> badge) that say how they were made.</p>
          <p>See something harmful or wrong? Use <b>Report</b> on any reply or file. You can follow your reports in <Link to="/settings/safety#reports" onClick={() => setOpen(false)}>Settings › Safety &amp; security</Link>.</p>
        </div>
      </Dialog>
    </>
  );
}

/* ---------- install (DEV-06) ---------- */
export function InstallButton({ compact }: { compact?: boolean }) {
  const p = useInstallPrompt();
  const toast = useToast();
  if (!p.canInstall) return null;
  return (
    <Button size="sm" variant="ghost" icon={<Download size={14} aria-hidden="true" />} data-cap="DEV-06" aria-label="Install Crosspoint AI + as an app"
      onClick={() => void p.install().then((r) => { if (r === 'accepted') toast.show('Installed. Open it from your apps or taskbar.'); })}>
      {compact ? null : 'Install app'}
    </Button>
  );
}

/* ---------- reactive nav badges ---------- */
/** Re-reads a descriptor's `badge` getter (stores outside React) every couple of seconds, or uses its `useBadge` hook. */
const tick = { n: 0, subs: new Set<() => void>(), timer: 0 as unknown as ReturnType<typeof setInterval> | 0 };
function subscribeTick(l: () => void) {
  tick.subs.add(l);
  if (tick.subs.size === 1) tick.timer = setInterval(() => { tick.n++; tick.subs.forEach((s) => s()); }, 2000);
  return () => { tick.subs.delete(l); if (!tick.subs.size && tick.timer) { clearInterval(tick.timer); tick.timer = 0; } };
}
const getTick = () => tick.n;

function HookBadge({ use }: { use: () => number | undefined }) {
  const n = use();
  return n ? <span className="badge" aria-label={`${n} waiting`}>{n}</span> : null;
}
function GetterBadge({ get }: { get: () => number | undefined }) {
  useSyncExternalStore(subscribeTick, getTick, getTick);
  const n = get();
  return n ? <span className="badge" aria-label={`${n} waiting`}>{n}</span> : null;
}
export function NavBadge({ f }: { f: FeatureDescriptor }) {
  if (f.useBadge) return <HookBadge use={f.useBadge} />;
  if (f.badge) return <GetterBadge get={f.badge} />;
  return null;
}
